import React, { ChangeEvent, useEffect, useState, useActionState } from 'react'
//@ts-ignore
import { Badge, Button, Input, Radio, Textarea } from 'dsm-react-lib'
import { Form, FormFeedback, Spinner } from 'reactstrap'
//@ts-ignore
import Select from 'react-select'
import { useForm, Controller } from 'react-hook-form'
import { useAvatarsList } from './constants'
import { getAvatarDisplayUrl, getInitials, selectThemeColors } from '../../../../utility/Utils'

import { useDispatch, useSelector } from 'react-redux'
import { setAgentDetails } from '../../../../_redux/actions/agentActions'
import { useAgentService } from './agentService'
import { toast } from 'react-toastify'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import { useAgentContext } from './AgentFormContext'
import {
  IconPhotoMinus,
  IconPhotoPlus,
  IconRobotFace,
  IconWand,
  IconRefresh,
  IconX,
  IconChevronLeft,
  IconCheck,
  IconArrowRight,
  IconBrain,
  IconWorldSearch
} from '@tabler/icons-react'
// @ts-ignore
import classnames from 'classnames'
import _endpoints from '../../../../_endpoints'
import { useTeamService } from '../../Teams/services/teamService'
import { Role } from '../../../../utility/types'
import { filterChatModels } from '../utils/constants'

export const accessSettingsOptions = [
  {
    id: 1,
    label: 'Personal',
    value: 'Personal',
    color: 'var(--color-secondary)'
  },
  {
    id: 2,
    label: 'Team',
    value: 'Team',
    color: 'var(--team-agent)'
  },
  {
    id: 3,
    label: 'Enterprise',
    value: 'Enterprise',
    color: 'var(--enterprise-agent)'
  }
]

const validationMessages = {
  assistantName: 'Assistant name is mandatory',
  appUrl: {
    requiredMessage: 'App url is mandatory',
    validationMessage: 'App url should start with https'
  },
  categories: 'Categories are mandatory',
  tags: 'Tags are mandatory',
  shortDescription: 'Short description is mandatory',
  feature: 'At least one feature is mandatory'
}

const AgentBasicDetails = ({
  setStep,
  setIsDeleteModalOpen,
  avatarFile,
  setAvatarFile,
  isValidationTriggered,
  agentNameValidationRef,
  onValidationCallback
}: any) => {
  const { chatModels } = useSelector((state: any) => state.chatbotReducer)
  const { register, errors, trigger, control, clearErrors, handleSubmit } = useForm({})
  const { agent } = useSelector((state: any) => state.agentReducer)
  const { roles } = useSelector((state: any) => state.userContextReducer.userContext)
  const isCentraliAdmin = roles?.includes(Role.Admin)
  const avatarsList = useAvatarsList()
  const userProfile = JSON.parse(localStorage.getItem('userInfoData') || '{}')
  const userSid = userProfile?.sid
  const { teamId: currentTeamId } = useParams<{ teamId: string }>()

  // Use context instead of local state
  const {
    assistantName,
    setAssistantName,
    selectedAvatar,
    setSelectedAvatar,
    assistantDescription,
    setAssistantDescription,
    selectedChatModel,
    setSelectedChatModel,
    instructions,
    setInstructions,
    selectedAccessOption,
    setSelectedAccessOption,
    isMemoryEnabled,
    setIsMemoryEnabled,
    isWebMCPEnabled,
    setIsWebMCPEnabled
  } = useAgentContext()

  const [showModelError, setShowModelError] = useState(false)
  const [showTeamError, setShowTeamError] = useState(false)
  const [predefinedAvatarsList, setPredefinedAvatarsList] = useState<any>(avatarsList)
  const [selectAssistantAvatar, setSelectAssistantAvatar] = useState(false)
  const [chatModelOptions, setChatModelOptions] = useState<any>([])
  const { isGenerating, generateSystemPrompt } = useAgentService()
  const { createAgent, updateAgentDetails, getAgentsListByUser, getModels } = useAgentService()
  const navigate = useNavigate()
  const location = useLocation()
  const { isFetchingTeams, getAllTeams } = useTeamService()
  const [teams, setTeamsData] = useState<any>(null)
  const [teamId, setTeamId] = useState<string | null>(null)
  const dispatch = useDispatch()
  // Agents created from the Community team's details page are always Enterprise-visible and can't be scoped to a team.
  const isCommunityTeamPage =
    location.pathname.toLowerCase().includes('teams') &&
    !!currentTeamId &&
    teams?.some((team: any) => team.id === currentTeamId && team.name === 'Community')

  // Helper function to convert URL to File
  const urlToFile = async (url: string, filename: string): Promise<File | null> => {
    try {
      const response = await fetch(url)
      if (!response.ok) throw new Error(`Failed to fetch ${url}`)

      const blob = await response.blob()
      const mimeType = blob.type || 'image/png'
      const extension = mimeType.split('/')[1] || 'png'

      return new File([blob], `${filename}.${extension}`, { type: mimeType })
    } catch (error) {
      console.error('Error converting URL to file:', error)
      return null
    }
  }

  useEffect(() => {
    // Check if URL contains 'teams' and set access option to 'Team'
    if (location.pathname.toLowerCase().includes('teams')) {
      setSelectedAccessOption('Team')
    }
    const getTeams = async () => {
      try {
        const response = await getAllTeams(0, 100)
        setTeamsData(
          (response?.items || []).filter(
            (team: any) =>
              team.name === 'Community' ||
              (team.userRole !== null && team.userRole !== 'Member' && team.status === 'Active')
          )
        )
      } catch (error) {
        setTeamsData([])
      }
    }
    getTeams()
  }, [])

  useEffect(() => {
    if (agent && agent.agentType === 'Team') {
      const matchingTeam = teams?.find((team: any) => team.id === agent?.scopeId)
      if (matchingTeam) {
        setTeamId(agent.scopeId)
        // Backfill smartDocsTeamId onto the agent so downstream steps
        if (!agent?.smartDocsTeamId && matchingTeam.smartDocsTeamId) {
          dispatch(setAgentDetails({ ...agent, smartDocsTeamId: matchingTeam.smartDocsTeamId }))
        }
      }
    }
    // Also set teamId from URL params if we're on teams page
    if (currentTeamId && teams && location.pathname.toLowerCase().includes('teams')) {
      const currentTeam = teams.find((team: any) => team.id === currentTeamId)
      if (currentTeam?.name === 'Community') {
        setSelectedAccessOption('Enterprise')
      } else {
        setTeamId(currentTeamId)
      }
    }
  }, [teams, currentTeamId])

  useEffect(() => {
    const fetchModels = async () => {
      const mappedModels =
        filterChatModels(chatModels).map((model: any) => ({
          id: model?.id,
          label: model?.displayName,
          value: model?.name,
          description: model?.description
        })) || []
      setChatModelOptions(mappedModels)
    }
    // Fetch models only if not already in redux store
    //getModels: api call to fetch models and set in redux
    if (chatModels === null) {
      getModels()
    } else {
      fetchModels()
    }
  }, [chatModels])

  useEffect(() => {
    if ((agent?.modelId || agent?.model?.id) && chatModelOptions?.length > 0) {
      const matchingModel = chatModelOptions.find(
        (option: any) => option.id === (agent.modelId || agent?.model?.id)
      )
      if (matchingModel) {
        setSelectedChatModel(matchingModel)
      }
    }
  }, [agent, chatModelOptions])

  useEffect(() => {
    if (isValidationTriggered) {
      trigger()
      // Check and set error states for custom validations
      if (!selectedChatModel) {
        setShowModelError(true)
      }
      if (selectedAccessOption === 'Team' && !teamId) {
        setShowTeamError(true)
      }
    }
  }, [isValidationTriggered, selectedChatModel, selectedAccessOption])

  // For prompt box above PillBadge
  const [showPromptBox, setShowPromptBox] = useState(false)
  const [isImprovingWithAI, setIsImprovingWithAI] = useState(false)
  const [promptText, setPromptText] = useState('')
  const [isPromptDescValid, setIsPromptDescValid] = useState(true)
  const [originalDescription, setOriginalDescription] = useState('')
  const [aiImprovedDescription, setAiImprovedDescription] = useState('')
  const [isLoadingAiResponse, setIsLoadingAiResponse] = useState(false)

  // For AI improvement of system prompt
  const [originalPromptText, setOriginalPromptText] = useState('')
  const [aiImprovedPrompt, setAiImprovedPrompt] = useState('')
  const [isLoadingPromptResponse, setIsLoadingPromptResponse] = useState(false)

  const handleClearAll = async () => {
    setAssistantName('')
    clearErrors('assistantName')
    // Convert default avatar to file
    const defaultAvatarItem = avatarsList?.find((item: any) => item.isSelected)
    if (defaultAvatarItem?.icon) {
      const avatarFile = await urlToFile(defaultAvatarItem.icon, 'default-avatar')
      if (avatarFile) {
        setAvatarFile(avatarFile)
        setSelectedAvatar(avatarFile)
      } else {
        setSelectedAvatar(defaultAvatarItem.icon)
        setAvatarFile(null)
      }
    }
    setAssistantDescription('')
    setSelectedChatModel(null)
    setShowModelError(false)
    setInstructions('')
    setPromptText('')
    setPredefinedAvatarsList(avatarsList)
    setSelectAssistantAvatar(false)
    setIsMemoryEnabled(false)
    if (location.pathname.toLowerCase().includes('agents')) setSelectedAccessOption('Personal')
    dispatch(setAgentDetails({ ...agent, skills: [] }))
  }

  const selectAvatar = async (avatar: any) => {
    const selectedAvatarIcon: any = predefinedAvatarsList?.map((obj: any) => {
      return { ...obj, isSelected: obj.id === avatar.id }
    })
    setPredefinedAvatarsList(selectedAvatarIcon)

    const selectedItem = selectedAvatarIcon?.find((item: any) => item.isSelected)

    if (selectedItem?.name === 'defaultAvatar' || selectedItem?.icon === null) {
      setSelectedAvatar(null)
      setAvatarFile(null)
    } else if (selectedItem?.icon) {
      // Convert URL to File object for multipart upload
      const avatarFile = await urlToFile(selectedItem.icon, `avatar-${selectedItem.id}`)
      if (avatarFile) {
        setAvatarFile(avatarFile)
        setSelectedAvatar(avatarFile)
      } else {
        // Fallback to URL if conversion fails
        setSelectedAvatar(selectedItem.icon)
        setAvatarFile(null)
      }
    }
  }

  const customStyles = {
    control: (base: any, state: any) => ({
      ...base,
      height: '50px'
    }),
    option: (base: any, state: any) => ({
      ...base,
      padding: '8px 12px'
    })
  }

  // Custom option component to display model name and description
  const formatOptionLabel = (option: any, { context }: any) => {
    if (context === 'value') {
      return <div className="font-medium">{option.label}</div>
    }

    const isCurrentlySelected = selectedChatModel && option.id === selectedChatModel.id

    return (
      <div>
        <div className="font-medium">{option.label}</div>
        {option.description && (
          <div
            className={`text-xs mt-50 ${
              isCurrentlySelected ? 'text-[var(--brand-accent-color)]' : 'theme-text-subheading'
            }`}>
            {option.description}
          </div>
        )}
      </div>
    )
  }

  const handleAssistantNameChange = (event: any) => {
    const name = event?.target?.value
    // If only spaces, set to empty string; else, keep as-is
    setAssistantName(name)

    if (name && name.trim() !== '') {
      clearErrors('assistantName')
    }
    if (isValidationTriggered) {
      trigger('assistantName')
    }
  }

  const handleModelChange = (option: any) => {
    setSelectedChatModel(option)
    if (option) {
      setShowModelError(false)
      // Clear the form validation error for chatModel when a model is selected
      clearErrors('chatModel')
    }
    if (isValidationTriggered) {
      trigger('chatModel')
    }
  }

  const handleFileUpload = (uploadedFile: any) => {
    if (uploadedFile) {
      // If uploadedFile is already a File object
      if (uploadedFile instanceof File) {
        setAvatarFile(uploadedFile)
        setSelectedAvatar(uploadedFile)
      } else {
        const updatedList = predefinedAvatarsList.map((obj: any) => ({
          ...obj,
          isSelected: obj.id === uploadedFile.id
        }))
        setPredefinedAvatarsList(updatedList)
        // If uploadedFile has an icon property or other structure
        if (uploadedFile.icon && uploadedFile.icon instanceof File) {
          setAvatarFile(uploadedFile.icon)
          setSelectedAvatar(uploadedFile.icon)
        } else if (uploadedFile?.name === 'defaultAvatar') {
          setSelectedAvatar(null)
          setAvatarFile(null)
        } else {
          setSelectedAvatar(uploadedFile?.icon || URL?.createObjectURL(uploadedFile))
          selectAvatar(uploadedFile)
        }
      }
    }
  }

  const generateSystemInstructions = async (prompt: string) => {
    if (prompt.trim() === '') {
      setIsPromptDescValid(false)
      return
    }
    return await generateSystemPrompt(prompt, 'systemPrompt')
  }

  const enhanceAgentDescription = async (prompt: string) => {
    if (prompt.trim() === '') {
      setIsPromptDescValid(false)
      return
    }
    return await generateSystemPrompt(prompt)
  }

  const validateAppDetails = async () => {
    // Trigger form validation
    const isFormValid = await trigger()

    // Check required fields
    const isNameValid = assistantName?.trim() !== '' && assistantName !== undefined
    const isModelValid = selectedChatModel !== null && selectedChatModel !== undefined
    const isTeamValid =
      selectedAccessOption !== 'Team' || (selectedAccessOption === 'Team' && teamId)

    // Set error states
    if (!isModelValid) {
      setShowModelError(true)
    }
    if (selectedAccessOption === 'Team' && !teamId) {
      setShowTeamError(true)
    }

    // Combine all validation results
    const isValid = isFormValid && isNameValid && isModelValid && isTeamValid

    if (isValid) {
      dispatch(
        setAgentDetails({
          ...agent,
          name: assistantName?.trim(),
          avatar: selectedAvatar,
          description: assistantDescription,
          model: selectedChatModel,
          instructions,
          agentType: selectedAccessOption,
          skills: agent?.skills || [],
          scopeId: selectedAccessOption === 'Team' ? teamId : userSid,
          // Already known from the `teams` list fetched above
          smartDocsTeamId:
            selectedAccessOption === 'Team'
              ? teams?.find((team: any) => team.id === teamId)?.smartDocsTeamId
              : undefined,
          enableMemory: isMemoryEnabled
        })
      )
      return true
    }
    return false
  }

  const onNext = async () => {
    agentNameValidationRef.current = validateAppDetails
    const isFormValidated = await validateForm()

    dispatch(
      setAgentDetails({
        ...agent,
        id: agent?.id,
        name: assistantName?.trim(),
        avatar: selectedAvatar,
        description: assistantDescription,
        model: selectedChatModel,
        instructions,
        agentType: selectedAccessOption,
        skills: agent?.skills || [],
        scopeId: selectedAccessOption === 'Team' ? teamId : userSid,
        smartDocsTeamId:
          selectedAccessOption === 'Team'
            ? teams?.find((team: any) => team.id === teamId)?.smartDocsTeamId
            : undefined,
        enableMemory: isMemoryEnabled
      })
    )
    if (isFormValidated) {
      setStep('tools')
    }
  }

  const validateForm = async () => {
    const isFormValid = await trigger()

    // Check required fields
    const isModelValid = selectedChatModel !== null && selectedChatModel !== undefined
    const isTeamValid =
      selectedAccessOption !== 'Team' || (selectedAccessOption === 'Team' && teamId)

    // Set error states
    if (!isModelValid) {
      setShowModelError(true)
    }
    if (selectedAccessOption === 'Team' && !teamId) {
      setShowTeamError(true)
    }

    return isFormValid && isModelValid && isTeamValid
  }

  const [, submitAgent, isSubmitting] = useActionState(async (_prevState: null, data: any) => {
    const agentData = {
      ...data,
      avatarFile
    }
    const response = agent?.id
      ? await updateAgentDetails(agent.id, agentData)
      : await createAgent(agentData)
    if (response?.agent) {
      await getAgentsListByUser()
      if (response?.agent) {
        toast.success('Agent saved successfully')
      } else {
        toast.error('Error saving agent')
      }
      if (location?.pathname.includes('teams')) {
        navigate(`/teams/${currentTeamId}`)
      } else {
        navigate('/manage-agents')
      }
    } else {
      toast.error(response?.message)
    }
    return null
  }, null)

  const onSubmit = async () => {
    const isFormValidated = await validateForm()
    onValidationCallback()

    if (isFormValidated) {
      const data = {
        id: agent?.id,
        name: assistantName?.trim(),
        avatar: selectedAvatar,
        description: assistantDescription,
        model: selectedChatModel,
        instructions,
        agentType: selectedAccessOption,
        skills: agent?.skills || [],
        scopeId: selectedAccessOption === 'Team' ? teamId : userSid,
        smartDocsTeamId:
          selectedAccessOption === 'Team'
            ? teams?.find((team: any) => team.id === teamId)?.smartDocsTeamId
            : undefined,
        isPersonalAssistant: agent && agent?.isPersonalAssistant,
        enableMemory: isMemoryEnabled
      }

      submitAgent(data)
    }
  }

  const handleImproveDescription = async () => {
    if (assistantDescription.trim()) {
      setOriginalDescription(assistantDescription) // Store the original user input
      setIsImprovingWithAI(true)
      setIsLoadingAiResponse(true)

      try {
        const result: any = await enhanceAgentDescription(assistantDescription)
        const improvedText = result?.content || ''
        setAiImprovedDescription(improvedText)
        setAssistantDescription(improvedText) // Show AI response in textarea
        setIsLoadingAiResponse(false)
      } catch (error) {
        toast.error('Error generating improved description')
        setIsImprovingWithAI(false)
        setIsLoadingAiResponse(false)
      }
    }
  }

  const handleAcceptImprovement = () => {
    // Keep the AI improved description, clear the original
    setOriginalDescription('')
    setAiImprovedDescription('')
    setIsImprovingWithAI(false)
  }

  const handleDiscardImprovement = () => {
    // Revert to original user input
    setAssistantDescription(originalDescription)
    setOriginalDescription('')
    setAiImprovedDescription('')
    setIsImprovingWithAI(false)
  }

  const handleRegenerateImprovement = async () => {
    if (originalDescription.trim()) {
      setIsLoadingAiResponse(true)
      try {
        const result: any = await enhanceAgentDescription(originalDescription)
        const newImprovedText = result?.content || ''
        setAiImprovedDescription(newImprovedText)
        setAssistantDescription(newImprovedText) // Show new AI response in textarea
        setIsLoadingAiResponse(false)
      } catch (error) {
        toast.error('Error regenerating description')
        setIsLoadingAiResponse(false)
      }
    }
  }

  // System Prompt AI improvement handlers
  const handleGeneratePrompt = async () => {
    if (promptText.trim()) {
      setOriginalPromptText(promptText) // Store the original prompt input
      setIsLoadingPromptResponse(true)

      try {
        const result: any = await generateSystemInstructions(promptText)
        const improvedText = result?.content || ''
        setAiImprovedPrompt(improvedText)
        setInstructions(improvedText) // Show AI response in main textarea
        setIsLoadingPromptResponse(false)
      } catch (error) {
        toast.error('Error generating instructions')
        setIsLoadingPromptResponse(false)
      }
    }
  }

  const handleAcceptPrompt = () => {
    // Keep the AI improved prompt, clear the prompt box
    setPromptText('')
    setOriginalPromptText('')
    setAiImprovedPrompt('')
    setShowPromptBox(false)
  }

  const handleDiscardPrompt = () => {
    // Clear both main textarea and prompt box
    setInstructions('')
    setPromptText('')
    setOriginalPromptText('')
    setAiImprovedPrompt('')
    setShowPromptBox(false)
  }

  const handleRegeneratePrompt = async () => {
    if (originalPromptText.trim()) {
      setIsLoadingPromptResponse(true)
      try {
        const result: any = await generateSystemInstructions(originalPromptText)
        const newImprovedText = result?.content || ''
        setAiImprovedPrompt(newImprovedText)
        setInstructions(newImprovedText) // Show new AI response in main textarea
        setIsLoadingPromptResponse(false)
      } catch (error) {
        toast.error('Error regenerating instructions')
        setIsLoadingPromptResponse(false)
      }
    }
  }

  return agent.id === null || (agent?.id && selectedChatModel) ? (
    <>
      <div className="py-1">
        <div className="w-full flex justify-content-center">
          <Form className="w-full" onSubmit={handleSubmit(onSubmit)}>
            <div className="flex mb-2">
              <div className="mr-1 h-min flex justify-content-center align-items-center cursor-pointer relative group">
                {selectedAvatar ? (
                  <div className="relative">
                    <img
                      src={getAvatarDisplayUrl(selectedAvatar)}
                      width="85px"
                      height="85px"
                      alt="avatar"
                      data-testid="avatar-image"
                      className="rounded-circle"
                      onClick={() => setSelectAssistantAvatar(!selectAssistantAvatar)}
                    />
                    {/* Overlay background */}
                    <div className="absolute inset-0 bg-black opacity-0 group-hover:!opacity-50 rounded-full transition-opacity duration-200 pointer-events-none"></div>
                    <div className="absolute inset-0 opacity-0 group-hover:!opacity-100 rounded-full flex items-center justify-center transition-opacity duration-200 pointer-events-none">
                      <IconPhotoPlus size={32} color="white" />
                    </div>
                  </div>
                ) : assistantName !== '' ? (
                  <div className="relative">
                    <div
                      className="rounded-full w-[85px] h-[85px] flex items-center justify-center bg-primary text-white text-[2rem]"
                      data-testid="avatar-initials"
                      role="img"
                      aria-label="avatar"
                      onClick={() => setSelectAssistantAvatar(!selectAssistantAvatar)}>
                      {assistantName ? getInitials(assistantName).slice(0, 2) : 'AI'}
                    </div>
                    <div className="absolute inset-0 bg-black opacity-0 group-hover:!opacity-50 rounded-full transition-opacity duration-200 pointer-events-none"></div>
                    <div className="absolute inset-0 opacity-0 group-hover:!opacity-100 rounded-full flex items-center justify-center transition-opacity duration-200 pointer-events-none">
                      <IconPhotoPlus size={32} color="white" />
                    </div>
                  </div>
                ) : (
                  <div className="relative">
                    <div
                      className="rounded-full w-[85px] h-[85px] flex items-center justify-center bg-[var(--surface-muted)] text-white text-[2rem]"
                      data-testid="avatar-placeholder"
                      role="img"
                      aria-label="avatar"
                      onClick={() => setSelectAssistantAvatar(!selectAssistantAvatar)}>
                      <IconRobotFace size={46} className="text-[var(--icon-muted)]" stroke={1.5} />
                    </div>
                    <div className="absolute inset-0 bg-black opacity-0 group-hover:!opacity-50 rounded-full transition-opacity duration-200 pointer-events-none"></div>
                    <div className="absolute inset-0 opacity-0 group-hover:!opacity-100 rounded-full flex items-center justify-center transition-opacity duration-200 pointer-events-none">
                      <IconPhotoPlus size={32} color="white" />
                    </div>
                  </div>
                )}
              </div>

              <div className="w-full ml-4">
                <h5 className="text-lg font-medium">Agent Name</h5>
                <Input
                  type="text"
                  id="assistantName"
                  name="assistantName"
                  placeholder="Enter a name for your agent"
                  value={assistantName}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    handleAssistantNameChange(event)
                  }
                  maxLength={100}
                  data-testid="assistantName"
                  ref={register({ required: true })}
                  invalid={
                    'assistantName' in errors || (assistantName && assistantName.trim() === '')
                  }
                  helperText={errors.assistantName ? validationMessages.assistantName : ''}
                />
              </div>
            </div>

            {/* Avatar Selection Panel */}
            {selectAssistantAvatar && (
              <div className="w-full mb-1 theme-surface-muted border theme-border-default rounded-lg p-1 transition-all duration-300 ease-in-out transform">
                <div className="flex justify-between items-center mb-1">
                  <h5 className="text-lg font-medium mb-0">
                    Choose a preset avatar or upload your own
                  </h5>
                  <IconX
                    size={20}
                    className="theme-text-subheading hover:text-[var(--text-heading)] cursor-pointer transition-colors"
                    onClick={() => setSelectAssistantAvatar(false)}
                  />
                </div>

                {/* Preset Avatars */}
                <div className="flex flex-wrap gap-1 mb-1">
                  {predefinedAvatarsList?.slice(0, 5)?.map((avatar: any, index: any) => (
                    <div
                      key={index}
                      onClick={() => selectAvatar(avatar)}
                      className={`w-12 h-12 rounded-full cursor-pointer border-2 transition-all duration-200 hover:scale-110 ${
                        avatar?.isSelected
                          ? 'border-(var(--color-primary)) shadow-lg'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}>
                      {avatar.icon === null ? (
                        <div className="w-full h-full rounded-full !bg-[var(--color-primary)] flex items-center justify-center text-white text-sm font-medium">
                          {assistantName ? getInitials(assistantName).slice(0, 2) : 'AI'}
                        </div>
                      ) : (
                        <img
                          src={avatar.icon}
                          alt={avatar.name || 'Avatar'}
                          className="w-full h-full rounded-full object-cover"
                        />
                      )}
                    </div>
                  ))}
                </div>

                {/* File Upload Section */}
                <div className="flex gap-1 text-center mb-1">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const fileInput = e.target as HTMLInputElement
                      const file = fileInput.files?.[0]
                      if (file) {
                        if (file.size > 5 * 1024 * 1024) {
                          toast.error('File size should not exceed 5MB')
                          // Reset file input so user can re-select the same file
                          fileInput.value = ''
                          return
                        }
                        setAvatarFile(file)
                        setSelectedAvatar(file)
                      }
                    }}
                    className="hidden"
                    id="avatar-upload"
                  />
                  <Button
                    label="Choose file..."
                    color="dark"
                    icon={<IconPhotoPlus size={16} />}
                    className="theme-avatar-fill-btn"
                    onClick={() => {
                      const fileInput = document.getElementById('avatar-upload') as HTMLInputElement
                      fileInput?.click()
                    }}
                  />
                  <Button
                    label="Remove image"
                    variant="outline"
                    color="dark"
                    icon={<IconPhotoMinus size={16} />}
                    className="theme-avatar-outline-btn"
                    onClick={() => {
                      setSelectedAvatar(null)
                      setAvatarFile(null)
                      // Reset to default avatar (initials)
                      const defaultAvatar = predefinedAvatarsList?.find(
                        (item: any) => item.name === 'defaultAvatar' || item.icon === null
                      )
                      if (defaultAvatar) {
                        selectAvatar(defaultAvatar)
                      }
                    }}
                  />
                </div>
              </div>
            )}

            <div className="w-full mb-2">
              <div className="flex justify-between items-center mb-1">
                <h5 className="text-lg font-medium">Description (optional)</h5>
                {assistantDescription !== '' && (
                  <div
                    className={`flex items-center gap-50 p-50 rounded transition-colors select-none ${
                      isLoadingAiResponse
                        ? 'theme-text-subheading theme-surface-muted cursor-not-allowed'
                        : 'text-[var(--brand-accent-color)] hover:bg-[var(--brand-accent-bg)] cursor-pointer'
                    }`}
                    onClick={!isLoadingAiResponse ? handleImproveDescription : undefined}>
                    <IconWand
                      className={
                        isLoadingAiResponse
                          ? 'theme-text-subheading'
                          : 'text-[var(--brand-accent-color)]'
                      }
                    />
                    <span>
                      {isLoadingAiResponse
                        ? 'Improving description...'
                        : isImprovingWithAI
                        ? 'Improve with AI'
                        : 'Improve with AI'}
                    </span>
                  </div>
                )}
              </div>
              <Textarea
                name="description"
                rows={4}
                placeholder="Enter a description for your assistant"
                onChange={(e: any) => {
                  setAssistantDescription(e?.target?.value)
                }}
                value={assistantDescription}
              />
              {isImprovingWithAI && (
                <div className="flex justify-end mt-0">
                  <div
                    className={`flex items-center gap-50 p-1 text-sm rounded transition-colors ${
                      isLoadingAiResponse
                        ? 'theme-text-subheading cursor-not-allowed'
                        : 'theme-text-body cursor-pointer theme-hover-surface'
                    }`}
                    onClick={!isLoadingAiResponse ? handleDiscardImprovement : undefined}>
                    <IconX size={18} />
                    <span>Discard</span>
                  </div>
                  <div
                    onClick={!isLoadingAiResponse ? handleRegenerateImprovement : undefined}
                    className={`flex items-center gap-50 p-1 text-sm rounded transition-colors ${
                      isLoadingAiResponse
                        ? 'theme-text-subheading cursor-not-allowed'
                        : 'theme-text-body cursor-pointer theme-hover-surface'
                    }`}>
                    <IconRefresh size={16} />
                    <span>Regenerate</span>
                  </div>
                  <div
                    onClick={!isLoadingAiResponse ? handleAcceptImprovement : undefined}
                    className={`flex items-center gap-50 p-1 text-sm rounded transition-colors ${
                      isLoadingAiResponse
                        ? 'theme-text-subheading cursor-not-allowed'
                        : 'theme-text-body cursor-pointer theme-hover-surface'
                    }`}>
                    <IconCheck size={18} />
                    <span>Accept</span>
                  </div>
                </div>
              )}
            </div>
            <div className="w-full mb-2">
              <h5 className="text-lg font-medium">Model</h5>
              {/* <Controller
                control={control}
                name="chatModel"
                rules={{ required: true }}
                render={() => ( */}
              <Select
                isClearable={false}
                name="chatModel"
                options={chatModelOptions}
                value={selectedChatModel}
                onChange={handleModelChange}
                classNamePrefix="select"
                placeholder="Select your model"
                menuPlacement="auto"
                styles={customStyles}
                formatOptionLabel={formatOptionLabel}
                getOptionLabel={(option: any) => option?.label || ''}
                getOptionValue={(option: any) => option?.id || ''}
                className={classnames('react-select', {
                  'is-invalid': showModelError && selectedChatModel === null
                })}
                theme={selectThemeColors}
              />
              {/* )}
              /> */}
              {showModelError && selectedChatModel === null && (
                <FormFeedback invalid={1}>Please select a model</FormFeedback>
              )}
            </div>

            <div className="w-full mb-2">
              {(location.pathname.toLowerCase().includes('agents') || isCommunityTeamPage) && (
                <>
                  <h5 className="text-lg font-medium">Visibility</h5>
                  <div className="flex flex-row gap-1">
                    {accessSettingsOptions
                      ?.filter((item: any) =>
                        isCommunityTeamPage
                          ? item.value === 'Enterprise'
                          : item.value !== 'Enterprise' || isCentraliAdmin
                      )
                      ?.map((item: any, index: any) => (
                        <div key={index} className="flex items-center gap-1">
                          <Radio
                            id={item.id}
                            variant="default"
                            name={item.name}
                            value={item.value}
                            data-radio-theme
                            disabled={
                              isCommunityTeamPage ||
                              location.pathname.toLowerCase().includes('edit-agent')
                            }
                            label={
                              <Badge
                                color={item.color}
                                className={`text-white rounded p-50 !font-normal`}
                                label={item.label}
                              />
                            }
                            checked={selectedAccessOption === item.value}
                            onChange={(e: any) => {
                              if (isCommunityTeamPage) return
                              // Update local state
                              setSelectedAccessOption(e?.target?.value)
                              // Clear team error and teamId when switching away from Team or Teams
                              if (e?.target?.value !== 'Team' && e?.target?.value !== 'Teams') {
                                setShowTeamError(false)
                                setTeamId(null)
                              }
                              // Update Redux state for live preview
                              dispatch(setAgentDetails({ ...agent, agentType: e?.target?.value }))
                            }}
                          />
                        </div>
                      ))}
                  </div>
                </>
              )}

              {/* Teams Dropdown - Show only when Team is selected (not for the Community team, which is always Enterprise) */}
              {!isCommunityTeamPage &&
                (selectedAccessOption === 'Team' ||
                  location.pathname.toLowerCase().includes('teams')) &&
                (() => {
                  const selectedTeamOption =
                    teamId && teams
                      ? teams
                          .map((team: any) => ({ label: team.name, value: team.id }))
                          .find((option: any) => option.value === teamId) || null
                      : null

                  // Editing an existing Team agent whose team hasn't resolved yet - show a loader instead of the "Select teams" placeholder, which would otherwise flash briefly before the real team name populates.
                  const isEditingTeamAgent = !!agent?.id && agent?.agentType === 'Team'
                  // New Team agent from /teams/:teamId/add-agent - teamId isn't set until `teams` loads.
                  const isNewTeamAgentFromTeamPage =
                    !agent?.id &&
                    !!currentTeamId &&
                    location.pathname.toLowerCase().includes('teams')
                  const isLoadingSelectedTeam =
                    (isEditingTeamAgent || isNewTeamAgentFromTeamPage) &&
                    !teamId &&
                    (teams === null || isFetchingTeams)

                  return (
                    <div className="mt-1">
                      <h5 className="text-lg font-medium">Select Team</h5>
                      <Select
                        isClearable={true}
                        isLoading={isLoadingSelectedTeam}
                        isDisabled={
                          isLoadingSelectedTeam ||
                          (selectedAccessOption === 'Team' &&
                            location.pathname !== '/agents/add-agent')
                        }
                        name="teams"
                        options={
                          teams?.map((team: any) => ({
                            label: team.name,
                            value: team.id
                          })) || []
                        }
                        value={selectedTeamOption}
                        classNamePrefix="select"
                        placeholder={isFetchingTeams ? 'Loading teams...' : 'Select team'}
                        theme={selectThemeColors}
                        menuPlacement="auto"
                        styles={customStyles}
                        onChange={(selectedTeam: any) => {
                          if (!location.pathname.toLowerCase().includes('teams')) {
                            setTeamId(selectedTeam?.value)
                            if (selectedTeam) {
                              setShowTeamError(false)
                            }
                          }
                        }}
                        className={classnames('react-select', {
                          'is-invalid':
                            showTeamError &&
                            (selectedAccessOption === 'Team' || selectedAccessOption === 'Teams') &&
                            !teamId
                        })}
                      />
                      {showTeamError &&
                        (selectedAccessOption === 'Team' || selectedAccessOption === 'Teams') &&
                        !teamId && <FormFeedback invalid={1}>Please select a team</FormFeedback>}
                    </div>
                  )
                })()}
            </div>
            <div className="w-full mb-2">
              <div className="flex justify-between items-center mb-50">
                <h5 className="text-lg font-medium mb-0">System Prompt</h5>
                <div
                  className={`flex items-center gap-50 p-50 rounded transition-colors select-none ${
                    isLoadingPromptResponse
                      ? 'theme-text-subheading theme-surface-muted cursor-not-allowed'
                      : 'text-[var(--brand-accent-color)] hover:bg-[var(--brand-accent-bg)] cursor-pointer'
                  }`}
                  onClick={
                    !isLoadingPromptResponse ? () => setShowPromptBox((v) => !v) : undefined
                  }>
                  <IconWand
                    className={
                      isLoadingPromptResponse
                        ? 'theme-text-subheading'
                        : 'text-[var(--brand-accent-color)]'
                    }
                  />
                  <span>{isLoadingPromptResponse ? 'Generating...' : 'Generate with AI'}</span>
                </div>
              </div>
              <Textarea
                name="instructions"
                rows={5}
                placeholder="Write or generate a system prompt for your agent"
                onChange={(e: any) => {
                  setInstructions(e?.target?.value)
                }}
                value={instructions}
                data-testid="instructions"
              />

              {showPromptBox && (
                <div className="mt-1 theme-surface-muted rounded-lg border theme-border-default p-1">
                  <Textarea
                    className="w-full theme-surface border border-[var(--border-default)] rounded-md p-1 resize-none"
                    value={promptText}
                    onChange={(e: any) => {
                      setPromptText(e.target.value)
                      if (e.target.value.trim() !== '') {
                        setIsPromptDescValid(true)
                      }
                    }}
                    placeholder="Describe what this agent should do or how it should behave e.g.: 'A data analyst that compares ESG metrics across portfolios and writes clear summaries.'"
                    rows={3}
                    invalid={!isPromptDescValid}
                    helperText={isPromptDescValid ? '' : 'Prompt description cannot be empty'}
                  />

                  <div className="flex justify-between items-center mt-2">
                    <Button
                      className="flex items-center gap-50 p-1 text-white rounded transition-colors"
                      onClick={handleGeneratePrompt}
                      color="gray"
                      icon={<IconWand color="white" size={16} />}
                      label="Generate prompt"
                      disabled={promptText === '' || isLoadingPromptResponse}
                    />

                    <div className="flex justify-end mt-0">
                      <div
                        className={`flex items-center gap-50 p-1 text-sm rounded transition-colors ${
                          isLoadingPromptResponse || !aiImprovedPrompt
                            ? 'theme-text-subheading cursor-not-allowed'
                            : 'theme-text-body cursor-pointer theme-hover-surface'
                        }`}
                        onClick={
                          !isLoadingPromptResponse && aiImprovedPrompt
                            ? handleDiscardPrompt
                            : undefined
                        }>
                        <IconX size={18} />
                        <span>Discard</span>
                      </div>
                      <div
                        onClick={
                          !isLoadingPromptResponse && aiImprovedPrompt
                            ? handleRegeneratePrompt
                            : undefined
                        }
                        className={`flex items-center gap-50 p-1 text-sm rounded transition-colors ${
                          isLoadingPromptResponse || !aiImprovedPrompt
                            ? 'theme-text-subheading cursor-not-allowed'
                            : 'theme-text-body cursor-pointer theme-hover-surface'
                        }`}>
                        <IconRefresh size={16} />
                        <span>{isLoadingPromptResponse ? 'Regenerating...' : 'Regenerate'}</span>
                      </div>
                      <div
                        onClick={
                          !isLoadingPromptResponse && aiImprovedPrompt
                            ? handleAcceptPrompt
                            : undefined
                        }
                        className={`flex items-center gap-50 p-1 text-sm rounded transition-colors ${
                          isLoadingPromptResponse || !aiImprovedPrompt
                            ? 'theme-text-subheading cursor-not-allowed'
                            : 'theme-text-body cursor-pointer theme-hover-surface'
                        }`}>
                        <IconCheck size={18} />
                        <span>Accept</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            {/* <h5 className="text-lg mt-2 font-medium">Skills</h5>
            <Select
              isClearable={true}
              isMulti
              name="skills"
              options={skillsOptions}
              value={selectedSkills}
              onChange={(selectedOption: any) => {
                setSelectedSkills(selectedOption)
              }}
              classNamePrefix="select"
              placeholder="Select skills"
              theme={selectThemeColors}
              menuPlacement="auto"
            /> */}

            {/* Memory toggle */}
            <div className="w-full mb-2 p-1 theme-surface-muted border theme-border-default rounded-lg">
              <div className="flex items-center justify-between">
                <IconBrain
                  className="me-2"
                  size={35}
                  color={isMemoryEnabled ? 'var(--brand-accent-color)' : 'currentColor'}
                />
                <div className="flex-1 me-2">
                  <h5 className="text-lg font-medium mb-0">Enable Memory</h5>
                  <p className="text-sm theme-text-subheading mb-0 mt-25">
                    When enabled, the agent retains key facts from conversations — such as user
                    preferences, past decisions, and recurring context — so it can provide more
                    personalised and consistent responses over time.
                  </p>
                </div>
                <input
                  type="checkbox"
                  className="switch"
                  checked={isMemoryEnabled}
                  onClick={() => setIsMemoryEnabled(!isMemoryEnabled)}
                />
              </div>
            </div>

            {/* Web search toggle */}
            <div className="w-full mb-2 p-1 theme-surface-muted border theme-border-default rounded-lg">
              <div className="flex items-center justify-between">
                <IconWorldSearch
                  className="me-2"
                  size={34}
                  color={isWebMCPEnabled ? 'var(--brand-accent-color)' : 'currentColor'}
                />
                <div className="flex-1 me-2">
                  <h5 className="text-lg font-medium mb-0">Enable Web Search</h5>
                  <p className="text-sm theme-text-subheading mb-0 mt-25">
                    When enabled, the agent can perform web searches to provide more accurate and
                    up-to-date information in its responses.
                  </p>
                </div>
                <input
                  type="checkbox"
                  className="switch"
                  checked={isWebMCPEnabled}
                  onClick={() => setIsWebMCPEnabled(!isWebMCPEnabled)}
                />
              </div>
            </div>

            <div className="flex justify-between items-center mt-1 mb-2">
              <button
                type="button"
                onClick={() => {
                  if (location?.pathname.includes('teams')) {
                    navigate(`/teams/${currentTeamId}`)
                  } else {
                    navigate('/manage-agents')
                  }
                }}
                className="flex items-center gap-50 rounded-md border theme-border-default bg-theme-surface-muted p-2.5 text-theme-heading transition-colors hover:bg-theme-hover hover:text-theme-brand-accent focus:outline-none focus:ring-1 focus:ring-theme-brand-accent">
                <IconChevronLeft size={16} />
                <span>Back to agents</span>
              </button>
              <div className="flex justify-end text-end">
                <div className="flex gap-1">
                  {agent?.id && !agent?.isPersonalAssistant ? (
                    <Button
                      label="Delete agent"
                      onClick={setIsDeleteModalOpen}
                      variant="outline"
                      color="danger"
                    />
                  ) : (
                    <Button
                      label="Clear all"
                      onClick={handleClearAll}
                      variant="flat"
                      color="primary"
                      style={{ color: 'var(--brand-accent-color)' }}
                    />
                  )}
                  <Button
                    label="Next"
                    iconPosition="end"
                    icon={<IconArrowRight size={18} />}
                    color="primary"
                    className="btn-next"
                    disabled={selectedAccessOption === 'Team' ? !teamId : undefined}
                    onClick={onNext}
                  />
                </div>
              </div>
            </div>
          </Form>
        </div>
      </div>
    </>
  ) : (
    <div className="flex items-center justify-center h-screen">
      <Spinner color="primary" />
    </div>
  )
}
export default AgentBasicDetails
